<template>
  <div class="card bg-gradient-to-r from-primary/10 to-secondary/10 card-bordered mb-6">
    <div class="card-body p-6">
      <div class="flex justify-between items-start">
        <div class="flex-1">
          <!-- Enhanced Competition Title -->
          <div class="mb-4">
            <h1 class="text-4xl font-bold mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              {{ competition.name }}
            </h1>
          </div>
          
          <!-- Key Highlights -->
          <div class="flex flex-wrap gap-6 text-sm items-center mb-4">
            <!-- Countdown Timer (before competition starts) -->
            <div v-if="!competition.is_locked && timeUntilStart" class="flex items-center gap-2 bg-primary/20 px-3 py-2 rounded-lg">
              <i class="mdi mdi-rocket-launch text-primary"></i>
              <span class="text-base-content/70">Starts in:</span>
              <span class="font-bold text-primary text-lg">{{ timeUntilStart }}</span>
            </div>
            
            <!-- Competition Status -->
            <div class="flex items-center gap-2">
              <span :class="competition.is_locked ? 'badge badge-error badge-lg' : 'badge badge-success badge-lg'">
                <i :class="competition.is_locked ? 'mdi mdi-play-circle' : 'mdi mdi-account-plus'"></i>
                {{ competition.is_locked ? 'Live Trading' : 'Open for Entries' }}
              </span>
            </div>
            
            <!-- Participant Count -->
            <div class="flex items-center gap-2">
              <i class="mdi mdi-account-group text-primary"></i>
              <span class="font-semibold">{{ participantCount || 0 }} participants</span>
            </div>
          </div>
          
          <!-- Market Hours Info -->
          <div class="bg-base-200/50 p-3 rounded-lg">
            <div class="flex items-center gap-2 mb-2">
              <i class="mdi mdi-clock-outline text-primary"></i>
              <span class="font-semibold text-sm">Market Hours</span>
            </div>
            <div class="grid md:grid-cols-2 gap-4 text-xs">
              <div>
                <span class="text-base-content/70">Trading Begins:</span>
                <span class="font-medium ml-1">{{ new Date(competition.start_date).toLocaleDateString() }} at 9:30 AM ET</span>
              </div>
              <div>
                <span class="text-base-content/70">Trading Ends:</span>
                <span class="font-medium ml-1">{{ getEndDate() }} at 4:00 PM ET</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Action Buttons -->
        <div v-if="showDeleteButton" class="flex gap-2">
          <button 
            @click="$emit('delete')" 
            class="btn btn-error btn-sm"
            :disabled="loading"
          >
            <span v-if="loading" class="loading loading-spinner loading-xs"></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  competition: {
    type: Object,
    required: true
  },
  participantCount: {
    type: Number,
    default: 0
  },
  showDeleteButton: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['delete'])

// Helper function to get end date
const getEndDate = () => {
  if (!props.competition) return ''
  const startDate = new Date(props.competition.start_date)
  const endDate = new Date(startDate.getTime() + (props.competition.duration_days * 24 * 60 * 60 * 1000))
  return endDate.toLocaleDateString()
}

// Countdown timer
const timeUntilStart = ref('')
let countdownInterval = null

const updateCountdown = () => {
  if (!props.competition || props.competition.is_locked) {
    timeUntilStart.value = ''
    return
  }

  const now = new Date()
  const startDate = new Date(props.competition.start_date)
  const timeDiff = startDate - now

  if (timeDiff <= 0) {
    timeUntilStart.value = 'Starting now...'
    return
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)

  if (days > 0) {
    timeUntilStart.value = `${days}d ${hours}h ${minutes}m`
  } else if (hours > 0) {
    timeUntilStart.value = `${hours}h ${minutes}m ${seconds}s`
  } else if (minutes > 0) {
    timeUntilStart.value = `${minutes}m ${seconds}s`
  } else {
    timeUntilStart.value = `${seconds}s`
  }
}

onMounted(() => {
  if (props.competition && !props.competition.is_locked) {
    updateCountdown()
    countdownInterval = setInterval(updateCountdown, 1000)
  }
})

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})

watch(() => props.competition?.is_locked, (isLocked) => {
  if (isLocked && countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  } else if (!isLocked && !countdownInterval) {
    updateCountdown()
    countdownInterval = setInterval(updateCountdown, 1000)
  }
})
</script> 